import * as z from 'zod';
/**
 * A YouTube video identifier: eleven characters of base64url alphabet.
 */
export const youTubeVideoIdSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{11}$/, 'Expected an eleven-character YouTube video id');
const HOSTS = new Set([
    'youtube.com',
    'www.youtube.com',
    'm.youtube.com',
    'music.youtube.com',
    'youtu.be',
    'www.youtu.be',
]);
/**
 * Pulls the video identifier out of whatever a parent pasted.
 *
 * Defined here rather than in the parent application because the backend
 * validates the same input, and a URL both ends parse differently is a URL that
 * resolves to one video on screen and another in the database.
 *
 * Deliberately generous about the input. A parent copying from a phone gets a
 * `youtu.be` short link with a tracking parameter; from a desktop, a `watch?v=`
 * with a timestamp; from a share sheet, sometimes surrounding whitespace. All of
 * those are the same video, and refusing them teaches people the feature is
 * broken.
 *
 * Parsed by hand rather than with `URL`, for the same reason as the pairing URI:
 * this package also runs on Hermes.
 */
export const parseYouTubeVideoId = (input) => {
    const trimmed = input.trim();
    if (trimmed === '') {
        return null;
    }
    // A bare id, which is what a parent gets from some share sheets.
    const bare = youTubeVideoIdSchema.safeParse(trimmed);
    if (bare.success) {
        return bare.data;
    }
    const withoutScheme = trimmed.replace(/^https?:\/\//i, '');
    const [authority = '', rest = ''] = splitOnce(withoutScheme, '/');
    if (!HOSTS.has(authority.toLowerCase())) {
        return null;
    }
    const [path = '', query = ''] = splitOnce(rest, '?');
    const candidate = isShortLink(authority) ? path : readPathCandidate(path, query);
    const parsed = youTubeVideoIdSchema.safeParse(candidate);
    return parsed.success ? parsed.data : null;
};
const isShortLink = (authority) => authority.toLowerCase().endsWith('youtu.be');
const readPathCandidate = (path, query) => {
    const segments = path.split('/').filter(Boolean);
    // `/embed/ID`, `/shorts/ID`, `/live/ID` and `/v/ID` all carry the id in place.
    if (segments.length >= 2 && ['embed', 'shorts', 'live', 'v'].includes(segments[0] ?? '')) {
        return segments[1] ?? '';
    }
    // `/watch?v=ID`
    for (const pair of query.split('&')) {
        const [key, value] = splitOnce(pair, '=');
        if (key === 'v' && value !== undefined) {
            return value;
        }
    }
    return '';
};
const splitOnce = (value, separator) => {
    const at = value.indexOf(separator);
    return at === -1 ? [value] : [value.slice(0, at), value.slice(at + separator.length)];
};
/**
 * How a resolved YouTube video is described before a parent approves it.
 *
 * `isMadeForKids` is not decoration. YouTube's Developer Policies require the
 * designation to be checked before embedding, and Made-for-Kids content to be
 * played with tracking disabled (SPEC.md §11.2).
 */
export const youTubeVideoSchema = z.object({
    videoId: youTubeVideoIdSchema,
    title: z.string().min(1).max(200),
    channelTitle: z.string().min(1).max(200),
    durationSeconds: z.int().nonnegative().nullable(),
    isMadeForKids: z.boolean(),
    isEmbeddable: z.boolean(),
});
//# sourceMappingURL=youtube.js.map